import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, Globe, Mic } from "lucide-react";
import { LOGO, API, scrollToId } from "../lib/site";

export default function Footer() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/leads/stats`)
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  return (
    <footer data-testid="footer" className="relative border-t border-white/10 bg-[#05070B] px-4 sm:px-6 lg:px-8 pt-20 pb-10 overflow-hidden">
      <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 h-[380px] w-[800px] rounded-full bg-cyan-500/6 blur-[140px] pointer-events-none" />
      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/95 ring-1 ring-cyan-400/40 shadow-[0_0_28px_rgba(0,240,255,0.25)]">
                <img src={LOGO} alt="TalkLogical logo" className="h-9 w-9 object-contain" />
              </span>
              <span className="font-display font-bold text-2xl tracking-tight text-slate-50">
                Talk<span className="text-cyan-400">Logical</span>
              </span>
            </div>
            <p className="mt-5 max-w-sm text-sm text-slate-500 leading-relaxed">
              AI-built websites and 24/7 AI voice agents for local businesses. You run your
              business — we make sure the internet and the phone never miss a customer.
            </p>
            {stats && (
              <div className="mt-7 flex gap-4" data-testid="footer-stats">
                <div className="flex items-center gap-2.5 rounded-full border border-cyan-400/25 bg-cyan-400/5 px-4 py-2">
                  <Globe className="h-3.5 w-3.5 text-cyan-400" />
                  <span className="text-xs font-mono text-cyan-200">
                    {stats.website_previews} preview requests
                  </span>
                </div>
                <div className="flex items-center gap-2.5 rounded-full border border-violet-400/25 bg-violet-400/5 px-4 py-2">
                  <Mic className="h-3.5 w-3.5 text-violet-300" />
                  <span className="text-xs font-mono text-violet-200">
                    {stats.voice_agents} agent inquiries
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div>
              <p className="text-xs font-mono font-semibold tracking-[0.25em] uppercase text-slate-500 mb-5">Products</p>
              <ul className="space-y-3.5">
                {[
                  { label: "AI Website Maker", id: "services", tid: "footer-link-website" },
                  { label: "AI Voice Agent", id: "voice-demo", tid: "footer-link-voice" },
                  { label: "Pricing", id: "pricing", tid: "footer-link-pricing" },
                ].map((l) => (
                  <li key={l.tid}>
                    <button data-testid={l.tid} onClick={() => scrollToId(l.id)} className="group flex items-center gap-1 text-sm text-slate-400 hover:text-cyan-300 transition-colors">
                      {l.label}
                      <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-mono font-semibold tracking-[0.25em] uppercase text-slate-500 mb-5">Get started</p>
              <ul className="space-y-3.5">
                <li>
                  <button data-testid="footer-link-free-preview" onClick={() => scrollToId("preview")} className="text-sm text-slate-400 hover:text-cyan-300 transition-colors">
                    Free website preview
                  </button>
                </li>
                <li>
                  <button data-testid="footer-link-voice-inquiry" onClick={() => scrollToId("voice-inquiry")} className="text-sm text-slate-400 hover:text-violet-300 transition-colors">
                    Voice agent inquiry
                  </button>
                </li>
                <li>
                  <button data-testid="footer-link-faq" onClick={() => scrollToId("faq")} className="text-sm text-slate-400 hover:text-cyan-300 transition-colors">
                    FAQ
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-mono font-semibold tracking-[0.25em] uppercase text-slate-500 mb-5">Contact</p>
              <ul className="space-y-3.5 text-sm text-slate-400">
                <li data-testid="footer-email">nik@talklogical.com</li>
                <li data-testid="footer-phone">+1 607 424 6143</li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,1)]" />
                  AI nodes operational
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 select-none overflow-hidden" data-testid="footer-wordmark">
          <p className="font-display font-extrabold tracking-tight leading-none text-center text-[13vw] lg:text-[9rem] text-white/[0.04]">
            TalkLogical
          </p>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/5 pt-8">
          <p className="text-xs text-slate-600">© {new Date().getFullYear()} TalkLogical. All rights reserved.</p>
          <p className="text-xs font-mono text-slate-600">Built by AI. Answered by AI. Loved by humans.</p>
        </div>
      </div>
    </footer>
  );
}
